/**
 * voyageBounds.js
 *
 * FT-284: pure validator util — extracted from UnderwayPhase.js:191-238 so it
 * can be reused by every in-voyage data modal (Position, Weather, Fuel, Note,
 * Operation Start) and unit-tested without rendering a modal.
 *
 * Pure function. NO React imports, NO Date.now() outside parsing user-provided
 * ISO strings, NO navigator.onLine. Side-effect-free.
 *
 * Contract (see FT-284 brief §9):
 *   validateAgainstVoyageBounds(timeOptions, voyage, vessel)
 *     timeOptions: { eventTime: string, delayReason?: { code: string, note?: string } }
 *     voyage:      { departureTime?: string, arrivalTime?: string, preferredTimezone?: string }
 *     vessel:      { settings?: { timezone?: string }, timezone?: string }
 *
 *   Returns { error: null }
 *   OR     { error: { kind, boundaryIso, tz, humanMessage } }
 *
 * Behaviour:
 *   1. Defensive parsing — bad ISO strings skip the boundary check (T29 regression).
 *   2. delayReason.code ∈ BOUNDARY_OVERRIDE_CODES → skip the boundary check.
 *   3. Strictly LESS THAN for departure (eventTimeMs === departureTimeMs is allowed).
 *   4. Strictly GREATER THAN for arrival (eventTimeMs === arrivalTimeMs is allowed).
 *   5. humanMessage is pre-formatted in the error object.
 */

/**
 * The two structured-reason codes that override the voyage-bounds check.
 *
 * Used in two places:
 *   - TimePickerModal.js — surfaces these in the structured-reasons dropdown.
 *   - validateAgainstVoyageBounds — skips the bounds check when the user picks
 *     one of these codes.
 *
 * Single source of truth — exported so TimePickerModal can import + reference
 * via array index to avoid drift.
 */
export const BOUNDARY_OVERRIDE_CODES = ['pre_departure_observation', 'post_arrival_log'];

/**
 * Standardised timezone fallback chain. Voyage-first (matches the boundary-error
 * fallback at UnderwayPhase.js:227-228 prior to FT-284 deletion).
 */
function resolveBoundaryTimezone(voyage, vessel) {
  return (
    voyage?.preferredTimezone
    || vessel?.settings?.timezone
    || vessel?.timezone
    || 'UTC'
  );
}

/**
 * Defensively parse an ISO string into a UTC ms timestamp.
 * Returns NaN on any failure; never throws.
 */
function safeParseIsoMs(iso) {
  if (!iso) return NaN;
  try {
    const ms = new Date(iso).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  } catch {
    return NaN;
  }
}

/**
 * Format an ISO timestamp as "DD Mon YYYY HH:MM" in UTC for the human message.
 * Falls back to the raw ISO if formatting fails.
 */
function formatBoundaryIsoForHumans(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
  } catch {
    return iso;
  }
}

/**
 * Validate an event's time against voyage departure/arrival boundaries.
 *
 * @param {Object} timeOptions  { eventTime, delayReason? }
 * @param {Object} voyage       { departureTime?, arrivalTime?, preferredTimezone? }
 * @param {Object} vessel       { settings?.timezone?, timezone? }
 * @returns {{ error: null } | { error: { kind, boundaryIso, tz, humanMessage } }}
 */
export function validateAgainstVoyageBounds(timeOptions, voyage, vessel) {
  // Bail out early if no timeOptions at all.
  if (!timeOptions || !timeOptions.eventTime) return { error: null };

  // Override codes bypass the boundary check entirely.
  const code = timeOptions.delayReason?.code;
  if (code && BOUNDARY_OVERRIDE_CODES.includes(code)) {
    return { error: null };
  }

  const eventTimeMs = safeParseIsoMs(timeOptions.eventTime);
  if (!Number.isFinite(eventTimeMs)) {
    // Bad event time → defer to the modal's inline future-time guard.
    return { error: null };
  }

  const departureTimeMs = safeParseIsoMs(voyage?.departureTime);
  const arrivalTimeMs = safeParseIsoMs(voyage?.arrivalTime);
  const tz = resolveBoundaryTimezone(voyage, vessel);

  // Strictly LESS THAN — events at exactly departureTime are allowed.
  if (Number.isFinite(departureTimeMs) && eventTimeMs < departureTimeMs) {
    const boundaryIso = voyage.departureTime;
    const boundaryHuman = formatBoundaryIsoForHumans(boundaryIso);
    return {
      error: {
        kind: 'before_departure',
        boundaryIso,
        tz,
        humanMessage: `Event time cannot be before voyage departure (${boundaryHuman} ${tz}). Use the >4h reason "Pre-departure observation" to override.`
      }
    };
  }

  // Strictly GREATER THAN — events at exactly arrivalTime are allowed.
  if (Number.isFinite(arrivalTimeMs) && eventTimeMs > arrivalTimeMs) {
    const boundaryIso = voyage.arrivalTime;
    const boundaryHuman = formatBoundaryIsoForHumans(boundaryIso);
    return {
      error: {
        kind: 'after_arrival',
        boundaryIso,
        tz,
        humanMessage: `Event time cannot be after voyage arrival (${boundaryHuman} ${tz}). Use the >4h reason "Post-arrival log" to override.`
      }
    };
  }

  return { error: null };
}
